import { getCountryField, getCountryFieldLayout } from '@wix/form-multiline-address';
import { addFieldToSchema, generateUUID } from '@wix/form-schema';
import React, { useCallback, useMemo } from 'react';
import { EXPERIMENTS, FIELD_TYPES } from '../../../constants';
import { useAddressForms } from '../../../contexts/address-forms-context';
import { useConfig } from '../../../contexts/config-context';
import { useFocusGroupFieldEvent } from '../../../contexts/events-context';
import { useExperiments } from '../../../contexts/experiments-context';
import { useFieldActions } from '../../../contexts/field-actions-context';
import { useFieldValidation } from '../../../contexts/field-validation-context';
import { useForm } from '../../../contexts/form-context';
import { createFormView } from '../../../services/form-view';
import { Form } from '../../../ui/form/components/form';
import { useFormFields } from '../../../ui/form/contexts/form-fields-context';
import { Widget } from '../../../ui/widget/widget';
import { applyMultilineAddressSettings } from '../../../utils/apply-multiline-address-settings';
import { overrideFieldIds } from '../../../utils/field-id-overrides';
import { ADDRESS_TARGETS, fromAtlasAddress } from './address';
import { AddressAutofillProvider } from './address-autofill-context';
import { CountryCodeProvider } from './country-code-context';
import styles from './multiline-address.module.scss';
import { removeGaps } from './remove-gaps';
const NO_VALUES = {};
const NO_ERRORS = [];
const FIELD_IDS = {
  country: generateUUID(),
  subdivision: generateUUID(),
  city: generateUUID(),
  address_line: generateUUID(),
  address_line_2: generateUUID(),
  postal_code: generateUUID(),
  street_name: generateUUID(),
  street_number: generateUUID(),
  apartment: generateUUID()
};
const MultilineAddress = _ref => {
  let {
    id,
    form: _form,
    value,
    onChange,
    onFieldBlur,
    onFieldFocus,
    errors,
    FieldLayout,
    fieldSettings,
    label,
    validation,
    target,
    autocompleteEnabled,
    required,
    fieldType,
    widgetHtml,
    widgetEnabled
  } = _ref;
  const {
    isExperimentEnabled
  } = useExperiments();
  const useFieldsV2 = isExperimentEnabled(EXPERIMENTS.USE_FIELDS_V2);
  const {
    getCountryAddressForm
  } = useAddressForms();
  const {
    validateFields: parentValidateFields
  } = useFieldValidation();
  const formSchema = useForm();
  const fields = useFormFields();
  const fieldActions = useFieldActions();
  const {
    isMobile,
    breakpoint
  } = useConfig();
  const template = getCountryAddressForm(value == null ? void 0 : value.country);
  const isMultilineAddressField = fieldType === FIELD_TYPES.MULTILINE_ADDRESS || fieldType === FIELD_TYPES.BOOKINGS_ADDRESS;
  const mlaCountryField = useMemo(() => {
    var _validation$predefine;
    return getCountryField(required, validation == null || (_validation$predefine = validation.predefined) == null ? void 0 : _validation$predefine.multilineAddressOptions);
  }, [validation, required]);
  const mlaCountryLayout = useMemo(() => getCountryFieldLayout(), []);
  const form = useMemo(() => {
    var _validation$predefine2;
    if (!isMultilineAddressField || !template) {
      return _form && {
        ..._form,
        fields: overrideFieldIds(_form.fields, FIELD_IDS)
      };
    }
    const baseForm = createFormView({
      useFieldsV2,
      path: target,
      parentId: id,
      includeHiddenFields: true,
      isMobile,
      breakpoint,
      form: applyMultilineAddressSettings(addFieldToSchema(template, mlaCountryField, mlaCountryLayout), fieldSettings, validation == null || (_validation$predefine2 = validation.predefined) == null ? void 0 : _validation$predefine2.multilineAddressOptions.fields)
    });
    const hasHiddenFields = baseForm.fields.some(field => field.properties.hidden);
    if (!hasHiddenFields) {
      return baseForm;
    }
    const formWithoutGaps = removeGaps(baseForm);
    return {
      ...formWithoutGaps,
      fields: formWithoutGaps.fields.filter(field => !field.properties.hidden)
    };
  }, [template, _form, fieldSettings, validation, target, mlaCountryField, mlaCountryLayout, isMultilineAddressField, useFieldsV2, id, isMobile, breakpoint]);
  const handleAddressSelect = useCallback(atlasAddress => {
    if (!value) {
      return;
    }
    const address = fromAtlasAddress(atlasAddress, isMultilineAddressField);
    const newValue = {
      ...address,
      [ADDRESS_TARGETS.COUNTRY]: address[ADDRESS_TARGETS.COUNTRY] ?? value[ADDRESS_TARGETS.COUNTRY]
    };
    onChange == null || onChange(newValue);
  }, [onChange, value, isMultilineAddressField]);
  const handleFieldChange = useCallback((_target, subFieldValue) => {
    if (isMultilineAddressField && _target === ADDRESS_TARGETS.COUNTRY && (value == null ? void 0 : value[_target]) !== subFieldValue) {
      onChange == null || onChange({
        [ADDRESS_TARGETS.COUNTRY]: subFieldValue
      });
      return;
    }
    onChange == null || onChange({
      ...value,
      [_target]: subFieldValue
    });
  }, [onChange, value, isMultilineAddressField]);
  const validateFields = useCallback(async fieldsToValidate => {
    if (!isMultilineAddressField) {
      return parentValidateFields(fieldsToValidate);
    }
    return parentValidateFields([...fieldsToValidate, {
      path: `${target}/${ADDRESS_TARGETS.COUNTRY}`,
      value: value == null ? void 0 : value.country
    }]);
  }, [value, target, parentValidateFields, isMultilineAddressField]);
  useFocusGroupFieldEvent(id, () => {
    var _form$fields;
    const firstError = errors == null ? void 0 : errors[0];
    const field = form == null || (_form$fields = form.fields) == null ? void 0 : _form$fields.find(f => 'target' in f && f.target === (firstError == null ? void 0 : firstError.errorPath));
    return field == null ? void 0 : field.id;
  });
  if (!form) {
    return null;
  }
  if (widgetHtml && widgetEnabled) {
    return /*#__PURE__*/React.createElement(FieldLayout, {
      fieldId: id
    }, /*#__PURE__*/React.createElement(Widget, {
      fieldId: id,
      widgetHtml: widgetHtml,
      onChange: onChange,
      value: value
    }));
  }
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id
  }, /*#__PURE__*/React.createElement(CountryCodeProvider, {
    countryCode: value == null ? void 0 : value.country
  }, /*#__PURE__*/React.createElement(AddressAutofillProvider, {
    autocompleteEnabled: autocompleteEnabled,
    onAddressSelect: handleAddressSelect
  }, /*#__PURE__*/React.createElement("fieldset", {
    className: styles.reset
  }, /*#__PURE__*/React.createElement("legend", {
    className: styles.visuallyHidden
  }, label), /*#__PURE__*/React.createElement(Form, {
    formSchema: formSchema,
    form: form,
    values: value ?? NO_VALUES,
    onFieldChange: handleFieldChange,
    onBlur: onFieldBlur,
    onFocus: onFieldFocus,
    errors: errors ?? NO_ERRORS,
    validateFields: validateFields,
    fields: fields,
    fieldActions: fieldActions
  })))));
};
export default MultilineAddress;
//# sourceMappingURL=multiline-address.js.map